<template>
  <div class="storage-accounts-list">
    <vx-card title="Haio Smart Storage Management">
      <!-- Stats Cards -->
      <div class="vx-row mb-6">
        <div class="vx-col w-1/4 pr-2">
          <vx-card class="text-center">
            <div class="text-grey text-sm">Total Accounts</div>
            <div class="text-2xl font-semibold">{{ statistics.total_accounts || 0 }}</div>
          </vx-card>
        </div>
        <div class="vx-col w-1/4 px-2">
          <vx-card class="text-center">
            <div class="text-grey text-sm">Active Accounts</div>
            <div class="text-2xl font-semibold text-success">{{ statistics.active_accounts || 0 }}</div>
          </vx-card>
        </div>
        <div class="vx-col w-1/4 px-2">
          <vx-card class="text-center">
            <div class="text-grey text-sm">Failed/Suspended</div>
            <div class="text-2xl font-semibold text-danger">{{ (statistics.failed_accounts || 0) + (statistics.suspended_accounts || 0) }}</div>
          </vx-card>
        </div>
        <div class="vx-col w-1/4 pl-2">
          <vx-card class="text-center">
            <div class="text-grey text-sm">Total Quota (GB)</div>
            <div class="text-2xl font-semibold">{{ statistics.total_quota_gb || 0 }}</div>
          </vx-card>
        </div>
      </div>

      <!-- Filters and Actions -->
      <div class="flex items-center justify-between mb-4">
        <div class="flex items-center space-x-4">
          <vs-input 
            v-model="searchQuery" 
            placeholder="Search accounts..." 
            icon="search"
            class="w-64"
            @input="debouncedSearch"
          />
          
          <vs-select 
            v-model="selectedStatus" 
            placeholder="Filter by status"
            class="w-48"
            @change="filterByStatus"
          >
            <vs-select-item value="" text="All Statuses" />
            <vs-select-item value="0" text="Creating" />
            <vs-select-item value="1" text="Active" />
            <vs-select-item value="2" text="Suspended" />
            <vs-select-item value="3" text="Failed" />
            <vs-select-item value="4" text="Deleting" />
          </vs-select>
          
          <vs-select 
            v-model="selectedUser" 
            placeholder="Filter by user"
            class="w-48"
            @change="filterByUser"
          >
            <vs-select-item value="" text="All Users" />
            <vs-select-item 
              v-for="user in uniqueUsers" 
              :key="user.id" 
              :value="user.id" 
              :text="`${user.first_name} ${user.last_name} (${user.email})`" 
            />
          </vs-select>
        </div>
        
        <div class="flex space-x-2">
          <vs-button @click="createNewAccount" color="primary">
            <feather-icon icon="PlusIcon" svgClasses="w-4 h-4" />
            <span class="ml-2">Create Account</span>
          </vs-button>
          
          <vs-button @click="manageUserLimits" color="secondary">
            <feather-icon icon="SettingsIcon" svgClasses="w-4 h-4" />
            <span class="ml-2">User Limits</span>
          </vs-button>
          
          <vs-button @click="refreshData" color="success">
            <feather-icon icon="RefreshCwIcon" svgClasses="w-4 h-4" />
            <span class="ml-2">Refresh</span>
          </vs-button>
        </div>
      </div>

      <!-- Data Table -->
      <vs-table 
        v-model="selected" 
        :data="storageAccounts"
        pagination
        :max-items="20"
        search
        :total="totalAccounts"
        @change-page="changePage"
      >
        <template slot="thead">
          <vs-th>User</vs-th>
          <vs-th>Account Title</vs-th>
          <vs-th>Account Name</vs-th>
          <vs-th>Quota (GB)</vs-th>
          <vs-th>Usage (GB)</vs-th>
          <vs-th>Containers</vs-th>
          <vs-th>Status</vs-th>
          <vs-th>Created</vs-th>
          <vs-th>Actions</vs-th>
        </template>

        <template slot-scope="{ data }">
          <vs-tr :data="tr" :key="indextr" v-for="(tr, indextr) in data">
            <vs-td :data="tr.user">
              <div class="flex items-center">
                <div class="flex flex-col">
                  <span class="font-medium">{{ tr.user ? `${tr.user.first_name} ${tr.user.last_name}` : 'N/A' }}</span>
                  <span class="text-grey text-sm">{{ tr.user ? tr.user.email : 'N/A' }}</span>
                </div>
              </div>
            </vs-td>
            
            <vs-td :data="tr.account_title">
              {{ tr.account_title }}
            </vs-td>
            
            <vs-td :data="tr.account_name">
              <code class="bg-primary-light text-primary px-2 py-1 rounded">{{ tr.account_name }}</code>
            </vs-td>
            
            <vs-td :data="tr.quota_gb">
              {{ tr.quota_gb }}
            </vs-td>
            
            <vs-td :data="tr.used_space_gb">
              <div class="flex items-center">
                <span>{{ tr.used_space_gb || 0 }}</span>
                <div class="ml-2 w-16 bg-grey-light rounded-full h-2">
                  <div 
                    class="h-2 rounded-full"
                    :class="getUsageColorClass(tr.used_space_gb, tr.quota_gb)"
                    :style="`width: ${getUsagePercentage(tr.used_space_gb, tr.quota_gb)}%`"
                  ></div>
                </div>
              </div>
            </vs-td>
            
            <vs-td :data="tr.containers_count">
              {{ tr.containers_count || 0 }}
            </vs-td>
            
            <vs-td :data="tr.status">
              <vs-chip 
                :color="getStatusColor(tr.status)" 
                class="font-medium"
              >
                {{ getStatusText(tr.status) }}
              </vs-chip>
            </vs-td>
            
            <vs-td :data="tr.created_at">
              {{ formatDate(tr.created_at) }}
            </vs-td>
            
            <vs-td>
              <div class="flex items-center space-x-2">
                <feather-icon 
                  icon="EyeIcon" 
                  svgClasses="w-4 h-4 cursor-pointer hover:text-primary" 
                  @click="viewAccount(tr)"
                  title="View Details"
                />
                <feather-icon 
                  icon="EditIcon" 
                  svgClasses="w-4 h-4 cursor-pointer hover:text-warning" 
                  @click="editAccount(tr)"
                  title="Edit Account"
                />
                <feather-icon 
                  icon="RefreshCwIcon" 
                  svgClasses="w-4 h-4 cursor-pointer hover:text-success" 
                  @click="syncAccount(tr)"
                  title="Sync Usage"
                  v-if="tr.status === 1"
                />
                <feather-icon 
                  icon="TrashIcon" 
                  svgClasses="w-4 h-4 cursor-pointer hover:text-danger" 
                  @click="deleteAccount(tr)"
                  title="Delete Account"
                />
              </div>
            </vs-td>
          </vs-tr>
        </template>
      </vs-table>
    </vx-card>

    <!-- Create Account Modal -->
    <CreateAccountModal 
      v-if="showCreateModal"
      @close="showCreateModal = false"
      @created="onAccountCreated"
    />

    <!-- Edit Account Modal -->
    <EditAccountModal
      v-if="showEditModal"
      :account="selectedAccount"
      @close="showEditModal = false"
      @updated="onAccountUpdated"
    />

    <!-- User Limits Modal -->
    <UserLimitsModal
      v-if="showUserLimitsModal"
      @close="showUserLimitsModal = false"
    />
  </div>
</template>

<script>
import debounce from 'lodash/debounce'
import CreateAccountModal from './components/CreateAccountModal.vue'
import EditAccountModal from './components/EditAccountModal.vue'
import UserLimitsModal from './components/UserLimitsModal.vue'

export default {
  name: 'StorageAccountsList',
  components: {
    CreateAccountModal,
    EditAccountModal,
    UserLimitsModal
  },
  data() {
    return {
      storageAccounts: [],
      statistics: {},
      uniqueUsers: [],
      selected: [],
      searchQuery: '',
      selectedStatus: '',
      selectedUser: '',
      currentPage: 1,
      totalAccounts: 0,
      loading: false,
      showCreateModal: false,
      showEditModal: false,
      showUserLimitsModal: false,
      selectedAccount: null
    }
  },
  computed: {
    debouncedSearch() {
      return debounce(this.loadStorageAccounts, 500)
    }
  },
  mounted() {
    this.loadStorageAccounts()
    this.loadStatistics()
  },
  methods: {
    async loadStorageAccounts() {
      this.loading = true
      try {
        const params = {
          page: this.currentPage,
          per_page: 20
        }

        if (this.selectedStatus) params.status = this.selectedStatus
        if (this.selectedUser) params.user_id = this.selectedUser
        if (this.searchQuery) params.search = this.searchQuery

        const response = await this.$http.get('/admin/haio-smart-storage', { params })
        
        if (response.data.status) {
          this.storageAccounts = response.data.params.data
          this.totalAccounts = response.data.params.total_items
          this.extractUniqueUsers()
        }
      } catch (error) {
        console.error('Error loading storage accounts:', error)
        this.$vs.notify({
          title: 'Error',
          text: 'Failed to load storage accounts',
          color: 'danger'
        })
      } finally {
        this.loading = false
      }
    },

    async loadStatistics() {
      try {
        const response = await this.$http.get('/admin/haio-smart-storage/statistics')
        if (response.data.status) {
          this.statistics = response.data.params
        }
      } catch (error) {
        console.error('Error loading statistics:', error)
      }
    },

    extractUniqueUsers() {
      const users = new Map()
      this.storageAccounts.forEach(account => {
        if (account.user && !users.has(account.user.id)) {
          users.set(account.user.id, account.user)
        }
      })
      this.uniqueUsers = Array.from(users.values())
    },

    filterByStatus() {
      this.currentPage = 1
      this.loadStorageAccounts()
    },

    filterByUser() {
      this.currentPage = 1
      this.loadStorageAccounts()
    },

    changePage(page) {
      this.currentPage = page
      this.loadStorageAccounts()
    },

    refreshData() {
      this.loadStorageAccounts()
      this.loadStatistics()
    },

    createNewAccount() {
      this.showCreateModal = true
    },

    viewAccount(account) {
      this.$router.push(`/haio-smart-storage/${account.id}`)
    },

    editAccount(account) {
      this.selectedAccount = account
      this.showEditModal = true
    },

    async syncAccount(account) {
      try {
        const response = await this.$http.post(`/admin/haio-smart-storage/${account.id}/sync`)
        if (response.data.status) {
          this.$vs.notify({
            title: 'Success',
            text: 'Sync request sent successfully',
            color: 'success'
          })
        }
      } catch (error) {
        this.$vs.notify({
          title: 'Error',
          text: 'Failed to sync account',
          color: 'danger'
        })
      }
    },

    deleteAccount(account) {
      this.$vs.dialog({
        type: 'confirm',
        color: 'danger',
        title: 'Confirm Delete',
        text: `Are you sure you want to delete storage account "${account.account_title}"?`,
        accept: async () => {
          try {
            const response = await this.$http.delete(`/admin/haio-smart-storage/${account.id}`)
            if (response.data.status) {
              this.$vs.notify({
                title: 'Success',
                text: 'Account deletion initiated',
                color: 'success'
              })
              this.loadStorageAccounts()
            }
          } catch (error) {
            this.$vs.notify({
              title: 'Error',
              text: 'Failed to delete account',
              color: 'danger'
            })
          }
        }
      })
    },

    manageUserLimits() {
      this.showUserLimitsModal = true
    },

    onAccountCreated() {
      this.showCreateModal = false
      this.loadStorageAccounts()
      this.loadStatistics()
    },

    onAccountUpdated() {
      this.showEditModal = false
      this.loadStorageAccounts()
    },

    getStatusColor(status) {
      const colors = {
        0: 'warning',  // Creating
        1: 'success',  // Active
        2: 'danger',   // Suspended
        3: 'danger',   // Failed
        4: 'warning'   // Deleting
      }
      return colors[status] || 'primary'
    },

    getStatusText(status) {
      const texts = {
        0: 'Creating',
        1: 'Active',
        2: 'Suspended',
        3: 'Failed',
        4: 'Deleting'
      }
      return texts[status] || 'Unknown'
    },

    getUsagePercentage(used, quota) {
      return quota > 0 ? Math.min((used / quota) * 100, 100) : 0
    },

    getUsageColorClass(used, quota) {
      const percentage = this.getUsagePercentage(used, quota)
      if (percentage < 60) return 'bg-success'
      if (percentage < 80) return 'bg-warning'
      return 'bg-danger'
    },

    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    }
  }
}
</script>

<style scoped>
.storage-accounts-list {
  padding: 1rem;
}
</style>